import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { ZoomableGroup, ComposableMap, Geographies, Geography } from "react-simple-maps";
import colors from "tailwindcss/colors";
import isoCodes from "./countryIso.json";
import { Tooltip } from "react-tooltip";
import { MapsTable } from "./mapsTable";
import { GetExpeditionStatsResponseDto } from "@/types/api/api-types";

function percentageToColor(value: number, maxValue: number, minValue: number) {
  const minLightness = 10; // Darker shade
  const maxLightness = 40; // Lighter shade
  const percentage = (value - minValue) / (maxValue - minValue) || 1;
  const lightness = percentage * (maxLightness - minLightness) + minLightness;
  return `hsl(240, 100%, ${100 - lightness}%)`;
}

export type aggregatedMapData = {
  iso: string;
  label: string;
  value: number;
};

type MapProps = {
  selectedPeriod?: GetExpeditionStatsResponseDto["data"];
};

export const Maps = ({ selectedPeriod }: MapProps) => {
  const { t } = useTranslation("boClients");
  const [content, setContent] = useState("");

  const mapData = useMemo(() => {
    if (!selectedPeriod) return [];
    const data: {
      [id: string]: aggregatedMapData;
    } = {};
    selectedPeriod.map((item) => {
      if (!data[item.deliveryInfo.country]) {
        data[item.deliveryInfo.country] = {
          iso: item.deliveryInfo.country || "",
          label: item.deliveryInfo.country,
          value: 1,
        };
      } else {
        data[item.deliveryInfo.country].value += 1;
      }
    });
    return Object.values(data);
  }, [selectedPeriod]);

  const minDelivery = useMemo(() => Math.min(...mapData.map((el) => el.value)), [mapData]);
  const maxDelivery = useMemo(() => Math.max(...mapData.map((el) => el.value)), [mapData]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-1 flex-col gap-5 divide-y rounded-xl border bg-white p-6 shadow-xs">
        <div className="flex flex-row items-center gap-2">
          <h2 className="test text-xl font-semibold text-black">
            {t("profileStatisticsMaps.title")}
          </h2>
        </div>
        <ComposableMap viewBox="30 50 800 480" className="hidden md:block">
          <ZoomableGroup>
            <Geographies geography="https://gist.githubusercontent.com/WunderBart/6a3c589643c1978fd5a9/raw/d5c98dc5be842e3b8272c96f13129ea8fbb6220e/world_by_iso_min_topo.json">
              {({ geographies }) =>
                geographies.map((geo) => {
                  const isoCode = isoCodes.find(
                    (el) => el.isoCode3 === geo.properties.iso,
                  )?.isoCode;
                  const geoValue = isoCode ? mapData.find((el) => el.iso === isoCode)?.value : 0;

                  const color = geoValue
                    ? percentageToColor(geoValue, maxDelivery, minDelivery)
                    : colors.gray[100];
                  return (
                    <Geography
                      data-tooltip-float
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => {
                        if (!geoValue) return;
                        setContent(`${geo.properties.name}: ${geoValue}`);
                      }}
                      onMouseLeave={() => {
                        setContent("");
                      }}
                      style={{
                        default: {
                          fill: color,
                          outline: "none",
                        },
                        pressed: {
                          outline: "none",
                        },
                        hover: {
                          fill: color,
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        <MapsTable mapData={mapData} />
        <Tooltip anchorSelect=".rsm-geography" place="top" data-tooltip-float>
          {content}
        </Tooltip>
      </div>
    </div>
  );
};
